import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { signOut, startAnonSession, useAuth } from '@/lib/auth/useAuth';
import { useUserProfile } from '@/lib/auth/useUserProfile';
import { useAllMessagesSubscription } from '@/lib/realtime/useAllMessagesSubscription';
import { useHashRouteWithQuery, navigate } from '@/lib/router/useHashRoute';
import { isSupabaseConfigured } from '@/lib/supabase';
import { formatCode, isValidCode, parseCode } from '@/lib/family-code/crockford';
import { Login } from '@/app/Login';
import { Home } from '@/app/Home';
import { SetupNotice } from '@/app/SetupNotice';
import { A2HSWalkthrough } from '@/components/A2HSWalkthrough';
import { PushPermissionPrompt } from '@/components/PushPermissionPrompt';
import { Sidebar } from '@/components/Sidebar';
import { registerServiceWorker } from '@/lib/push/subscribe';
import { useViewport } from '@/lib/viewport/useViewport';

// Code splitting (CLAUDE.md §12 v2.3, performance-audit-2026-05-15.md)
// Routes/screens that are NOT first-paint critical are lazy-loaded so the
// initial JS bundle only ships Login + Home (the cold-start path). Each lazy
// route is wrapped in <Suspense> with a minimal spinner fallback.
//
// Why these and not others:
//   • Onboarding    — only first-time users; ~19 KB raw
//   • Chat          — only after a contact is tapped; ~18 KB raw
//   • Settings      — only after Settings is tapped; ~11 KB raw
//   • FamilyCodeShow — only after "Aile kodumu göster" tap; ~8 KB raw
//   • FaceToFace    — stretch feature, alpha users may never open; ~75 KB raw
const Onboarding = lazy(() =>
  import('@/app/Onboarding').then((m) => ({ default: m.Onboarding })),
);
const Chat = lazy(() => import('@/app/Chat').then((m) => ({ default: m.Chat })));
const Settings = lazy(() => import('@/app/Settings').then((m) => ({ default: m.Settings })));
const FamilyCodeShow = lazy(() =>
  import('@/app/FamilyCodeShow').then((m) => ({ default: m.FamilyCodeShow })),
);
const FaceToFace = lazy(() =>
  import('@/features/face-to-face/FaceToFace').then((m) => ({ default: m.FaceToFace })),
);

/**
 * Inline fallback used by every lazy boundary — matches the auth-loading spinner.
 * Editorial polish: serif "Family" wordmark + champagne ornament instead of bare
 * "…" so the brief window between routes still feels on-brand.
 */
function LazyFallback() {
  return (
    <main
      role="status"
      aria-live="polite"
      className="min-h-screen bg-bg-app text-text-secondary font-sans flex flex-col items-center justify-center gap-3"
    >
      <span className="font-serif italic text-3xl text-accent-primary">Family</span>
      <span className="ornament-divider w-24" aria-hidden="true">
        <span>✦</span>
      </span>
    </main>
  );
}

/**
 * App router — hash-based, no React Router (CLAUDE.md §3 v2.2).
 *
 * Routing pyramid:
 *   1. .env missing → <SetupNotice>
 *   2. Loading session → tiny spinner
 *   3. No session → <Login>
 *   4. Has session, profile loading → spinner
 *   5. Has session, profile MISSING → <Onboarding>
 *   6. Has session + profile, route '/settings' → <Settings>
 *   7. Has session + profile, route '/family-code' → <FamilyCodeShow>
 *   8. Has session + profile, default → <Home>
 *
 * iPad two-pane (CLAUDE.md §7, A6): when `useViewport()` reports 'tablet'
 * AND the route is one of the two-pane-friendly screens (Home, Chat,
 * FamilyCodeShow, Settings), App wraps the route in a sidebar + main grid.
 *
 * Face-to-face escapes the wrapper and renders fullscreen because it does
 * its own split-screen orientation flipping (CLAUDE.md §7 face-to-face).
 * Same for Login / Onboarding / SetupNotice — pre-auth flows are always
 * single-column.
 */
export default function App() {
  const { t, i18n } = useTranslation();
  const { session, loading: authLoading } = useAuth();
  const { profile, loading: profileLoading, refresh: refreshProfile } = useUserProfile(session);
  const { path: route, query } = useHashRouteWithQuery();
  const viewport = useViewport();
  // Bug 15 (Oturum 7): tek-aile davet linki toast'ı için ephemeral state.
  // Profile var + davet linki tıklanırsa "zaten ailendesin" mesajı 4s görünür.
  const [inviteToast, setInviteToast] = useState<string | null>(null);

  // Oturum 9 02:45 — Onboarding sticky render flag.
  // Onboarding bir kez gösterildiyse, profile refresh sırasında LazyFallback'a
  // düşürme — Onboarding step state'i korunsun. Submit success → refreshProfile
  // çağrısı Onboarding'i unmount etmiyor artık. Profile defined olduğunda
  // reset edilir (sign out + tekrar sign in temiz start).
  const onboardingShownRef = useRef(false);

  // Bug 10 fix (Oturum 7): Home-level realtime subscription.
  // Yeni mesaj geldiğinde ContactList preview + unread badge + tab title flash.
  // Chat ekranı kapalıyken bile farkındalık sağlar. RLS otomatik filtreliyor —
  // kullanıcı sadece kendi conv'larındaki mesaj event'lerini alır.
  useAllMessagesSubscription(session);

  // Persist <html lang="..."> in sync with i18n for accessibility (screen readers)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = i18n.language;
    }
  }, [i18n.language]);

  // Bug 23 fix (Oturum 8) + Comprehensive Dil Audit R1 (Oturum 8, 14:55):
  // Profile yüklendiğinde UI dilini profile.ui_lang'a hizala — AMA yalnızca
  // login sonrası **ilk profile yüklenmesinde**. Sonraki profile change'leri
  // (Settings dil değişimi → refreshProfile) tetiklemez; aksi takdirde
  // kullanıcının seçimi geri alınır (race condition).
  // useRef flag profile null'a düştüğünde (sign out) resetlenir, yeni
  // login'de tekrar bir kez hizalama yapılır.
  const initialLangAlignDone = useRef(false);
  useEffect(() => {
    if (!profile) {
      initialLangAlignDone.current = false;
      return;
    }
    if (initialLangAlignDone.current) return;
    initialLangAlignDone.current = true;
    if (profile.ui_lang !== i18n.language) {
      void i18n.changeLanguage(profile.ui_lang);
    }
  }, [profile, i18n]);

  // Oturum 9 02:15 — Orphan anon session cleanup.
  //
  // Senaryo: DB reset (dev:reset-db) edildikten sonra browser'da hâlâ
  // localStorage'da eski anon session JWT'si kalır. App auth gate:
  //   session var + profile yok → Onboarding'i mount eder
  // Ama Begüm yeni test başlatıyor — Landing'i (dil chip + 2 CTA) görmek
  // istiyor. Bu cleanup: profile yoksa VE `family.onboardingMode` flag yoksa
  // (yani Begüm Login'i hiç geçmemiş, session orphan) → sign out + Landing.
  //
  // Flag VAR ise (kullanıcı az önce "Yeni aile başlat" veya "Aileme katıl"
  // tıkladı) → Onboarding akışı geçerli, dokunma.
  //
  // useRef ile tek seferlik çalışır, signOut sonrası session null → loop yok.
  const orphanCleanupDone = useRef(false);
  useEffect(() => {
    if (authLoading || profileLoading) return;
    if (!session || profile) return;
    if (orphanCleanupDone.current) return;
    if (typeof localStorage === 'undefined') return;
    const mode = localStorage.getItem('family.onboardingMode');
    const pendingInvite = localStorage.getItem('family.pendingInviteCode');
    // Oturum 9 03:45 — orphan detection genişlet.
    // 'create' mode → kullanıcı yeni aile başlatıyor, devam et.
    // 'join' mode + pendingInviteCode VAR → davet linki akışı geçerli, devam et.
    // 'join' mode + pendingInviteCode YOK → eski test artığı (örn. Begüm önceki
    //    test'te WhatsApp tıkladı, submit fail oldu, flag kaldı) → orphan,
    //    signOut + Landing → kullanıcı yeniden mod seçer.
    // mode YOK → orphan (mevcut davranış).
    if (mode === 'create') return;
    if (mode === 'join' && pendingInvite) return;
    orphanCleanupDone.current = true;
    console.log('App: orphan anon session detected, signing out', { mode, hasInvite: !!pendingInvite });
    void signOut();
  }, [session, profile, authLoading, profileLoading]);

  // Restore font scale preference on first render
  useEffect(() => {
    if (typeof localStorage !== 'undefined' && typeof document !== 'undefined') {
      const saved = localStorage.getItem('family.fontScale');
      if (saved === 'large' || saved === 'extra') {
        document.documentElement.dataset['fontScale'] = saved;
      }
    }
  }, []);

  // Register service worker (push + offline) — silent on failure (dev/no-HTTPS)
  // and idempotent across remounts. CLAUDE.md §6 madde 9, §6 madde 10.
  useEffect(() => {
    void registerServiceWorker();
  }, []);

  // Magic link / auth callback URL temizliği (Oturum 5 — Bug 1 fix, defense-in-depth).
  // Supabase JS SDK detectSessionInUrl async cleanup yapar, ama timing race
  // sebebiyle Onboarding mount edildiğinde URL'de access_token / type=magiclink
  // gibi parametreler kalmış olabilir. Onboarding useEffect bunları yanlışlıkla
  // "code" sanıp pre-fill etmesin diye, session detected olunca AGRESİF temizle.
  useEffect(() => {
    if (typeof window === 'undefined' || !session) return;
    const dirtyKeys = [
      'access_token',
      'refresh_token',
      'expires_in',
      'expires_at',
      'token_type',
      'type',
      'provider_token',
      'provider_refresh_token',
    ];
    const url = new URL(window.location.href);
    let changed = false;
    for (const k of dirtyKeys) {
      if (url.searchParams.has(k)) {
        url.searchParams.delete(k);
        changed = true;
      }
    }
    // Hash router uses "#/path" — Supabase magic link uses "#access_token=...".
    // Discriminate: if hash starts with "#/" leave it, otherwise clear auth fragments.
    const hash = window.location.hash;
    if (hash && !hash.startsWith('#/') && /access_token=|refresh_token=|type=magiclink|type=recovery/.test(hash)) {
      url.hash = '';
      changed = true;
    }
    if (changed) {
      window.history.replaceState({}, '', url.toString());
    }
  }, [session]);

  // Oturum 8 23:30 — WhatsApp davet linki auto-join.
  // `?code=XXXX-XXXX-XXXX` URL parametresi davet linkidir. İki şey yaparız:
  // 1. localStorage'a kod + onboardingMode='join' yaz (Onboarding okur, prefill)
  // 2. Session henüz yoksa: OTOMATIK startAnonSession() → App auth gate Login'i
  //    bypass eder, direkt Onboarding'i join mode'da mount eder.
  // Sonuç: anne WhatsApp linki tıklar → ~1sn loading → direkt Onboarding step 1.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const incoming = params.get('code');
    if (!incoming) return;
    const parsed = parseCode(incoming);
    if (!parsed || !isValidCode(formatCode(parsed))) return;
    const formatted = formatCode(parsed);
    localStorage.setItem('family.pendingInviteCode', formatted);
    localStorage.setItem('family.onboardingMode', 'join');
    // Session yok + auth ready ise otomatik anon session başlat (Landing atlanır).
    // Session varsa profile dolu user case'ini ayrı useEffect (line 198) handle eder.
    if (!session && !authLoading) {
      void startAnonSession();
    }
  }, [session, authLoading]);

  // Bug 15 (Oturum 7): Profile dolu kullanıcı davet linki tıklarsa
  // (örn: Begüm kendi tarayıcısında zaten signed in iken WhatsApp linki açtı)
  // tek-aile constraint (CLAUDE.md §15 D12) altında zaten ailededir →
  // toast + Home redirect, pendingInviteCode'u temizle.
  useEffect(() => {
    if (!session || !profile) return;
    if (typeof window === 'undefined') return;
    const pending = localStorage.getItem('family.pendingInviteCode');
    if (!pending) return;
    localStorage.removeItem('family.pendingInviteCode');
    setInviteToast(t('familyCode.alreadyMember'));
    const timer = window.setTimeout(() => setInviteToast(null), 4000);
    navigate('/');
    return () => window.clearTimeout(timer);
  }, [session, profile, t]);

  // Listen for SW deep-link postMessage triggered by notificationclick.
  // Routes the open client to the conversation associated with the tapped notification.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    function onSwMessage(event: MessageEvent) {
      const data = event.data as { type?: string; deepLink?: string } | undefined;
      if (data?.type === 'navigate' && typeof data.deepLink === 'string') {
        navigate(data.deepLink);
      }
    }
    navigator.serviceWorker.addEventListener('message', onSwMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onSwMessage);
  }, []);

  if (!isSupabaseConfigured) {
    return <SetupNotice />;
  }

  if (authLoading) {
    return <LazyFallback />;
  }

  // Bug 15: shared invite-toast overlay, rendered alongside whichever
  // authenticated branch ends up returning below.
  const inviteToastNode = inviteToast ? (
    <div
      role="status"
      aria-live="polite"
      className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl bg-accent-success/95 text-white font-serif italic shadow-warm-md backdrop-blur-sm max-w-sm text-center text-sm"
    >
      {inviteToast}
    </div>
  ) : null;

  if (!session) {
    return (
      <>
        <Login />
        {/* A2HS coach is shown pre-login too — install before signing in keeps
            the cold-start ergonomics simple for older users. */}
        <A2HSWalkthrough />
      </>
    );
  }

  // Authenticated, but no profile row yet → user must complete onboarding.
  //
  // Oturum 9 02:45 — Sticky Onboarding fix (Begüm 2. hesap step1↔2 loop bug).
  //
  // Mevcut profile fetch sırasında `profileLoading=true` → LazyFallback render
  // → Onboarding UNMOUNT → fetch tamam (profile null) → Onboarding REMOUNT
  // (step=1 reset) → kullanıcı Welcome'a geri atıldı sanıyor. Submit yapsa
  // bile success path'inde refreshProfile tetiklenir, aynı unmount/remount.
  //
  // Fix: Onboarding bir kez mount edildiyse, profile refresh sırasında
  // (loading=true ama profile hâlâ null) **Onboarding'i render'da tut**
  // → step state korunur, submit hata mesajları görünür kalır, loop biter.
  //
  // `onboardingShownRef` profile defined olunca resetlenir (sign out + tekrar
  // sign in akışında temiz başlar).
  if (!profile) {
    // İlk fetch loading sırasında LazyFallback göster — Onboarding henüz mount değil
    if (profileLoading && !onboardingShownRef.current) {
      return <LazyFallback />;
    }
    onboardingShownRef.current = true;
    return (
      <>
        <Suspense fallback={<LazyFallback />}>
          <Onboarding onComplete={refreshProfile} />
        </Suspense>
        <A2HSWalkthrough />
      </>
    );
  }
  // Profile defined → reset ref so next sign-out + sign-in starts clean
  onboardingShownRef.current = false;

  // Face-to-face escapes the two-pane wrapper on all viewports — it owns
  // the whole screen and rotates one half 180°. Same for any future
  // fullscreen mode. The check happens before the wrapper to keep the JSX
  // tree free of accidental constraints (max-width parents, etc.).
  //
  // Oturum 9 01:10 — `/face-to-face` (no contactId) is supported as a
  // standalone entry from Home's "🎙 Yüzyüze konuş" button (Begüm UX).
  // Without a contact, F2F still works: both panes default to myLang,
  // each side picks their language via the inline chip picker.
  const f2fMatch =
    route === '/face-to-face' || route.startsWith('/face-to-face/');
  const f2fContactId = route.startsWith('/face-to-face/')
    ? route.slice('/face-to-face/'.length)
    : undefined;
  if (f2fMatch) {
    return (
      <>
        <Suspense fallback={<LazyFallback />}>
          <FaceToFace contactId={f2fContactId} />
        </Suspense>
        {inviteToastNode}
        <A2HSWalkthrough />
        <PushPermissionPrompt />
      </>
    );
  }

  if (viewport === 'tablet') {
    // Right-pane chat is driven by `?c=<convId>` so the visible route stays
    // on the contact-list pane. If the route is already a chat URL (e.g.
    // deep-link from a notification), use that conversation id instead.
    const queryConvId = query.get('c');
    const pathConvId = route.startsWith('/chat/') ? route.slice('/chat/'.length) : '';
    const activeConversationId = queryConvId || pathConvId || null;

    function openConversationInPane(conversationId: string) {
      if (conversationId === '') {
        // Clear selection (logo tap) → back to Home in main pane.
        navigate('/');
        return;
      }
      navigate(`/?c=${conversationId}`);
    }

    return (
      <>
        <div className="grid grid-cols-[320px_1fr] h-screen overflow-hidden">
          <Sidebar
            activeConversationId={activeConversationId}
            onOpenConversation={openConversationInPane}
          />
          <main className="overflow-y-auto bg-bg-app">
            <Suspense fallback={<LazyFallback />}>{renderTabletMain(activeConversationId)}</Suspense>
          </main>
        </div>
        {inviteToastNode}
        <A2HSWalkthrough />
        <PushPermissionPrompt />
      </>
    );
  }

  return (
    <>
      <Suspense fallback={<LazyFallback />}>{renderRoute()}</Suspense>
      {/* Overlays — A2HS first paint priority, push prompt only inside the PWA. */}
      {inviteToastNode}
      <A2HSWalkthrough />
      <PushPermissionPrompt />
    </>
  );

  function renderRoute() {
    if (route === '/settings') return <Settings />;
    if (route === '/family-code') return <FamilyCodeShow />;
    if (route.startsWith('/chat/')) {
      const conversationId = route.slice('/chat/'.length);
      if (conversationId.length > 0) {
        return <Chat conversationId={conversationId} />;
      }
    }
    // profile narrowed non-null by outer `if (!profile) return ...` guard
    return <Home profile={profile!} />;
  }

  function renderTabletMain(activeConversationId: string | null) {
    // Settings on tablet: sidebar stays mounted on the left (ContactList compact
    // with `activeConversationId=null` since no chat is in focus when the user
    // is in Settings). The right pane wraps Settings in a narrower centered
    // column so the long single-column form doesn't sprawl across an iPad's
    // ~700px main pane. The Settings component itself stays untouched —
    // wrapping happens here so phone layout (which has no sidebar) keeps its
    // existing max-w-2xl breathing room.
    if (route === '/settings') {
      return (
        <div className="mx-auto max-w-md">
          <Settings />
        </div>
      );
    }
    if (route === '/family-code') return <FamilyCodeShow />;
    if (activeConversationId) {
      return <Chat conversationId={activeConversationId} />;
    }
    return <Home profile={profile!} />;
  }
}
